import { useEffect, useState } from "react";
import { Link, useNavigate } from "react-router-dom";
import { api, formatApiError } from "../lib/api";
import { toast } from "sonner";
import PortalLayout from "../portal/PortalLayout";
import { StatusPill, SERVICE_LABELS, formatDate } from "../portal/shared";
import { Button } from "../components/ui/button";
import { Input } from "../components/ui/input";
import { Label } from "../components/ui/label";
import { Textarea } from "../components/ui/textarea";
import { Switch } from "../components/ui/switch";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "../components/ui/select";
import { Plus, Phone, AlertTriangle, ArrowLeft, CheckCircle2 } from "lucide-react";
import { BRAND } from "../lib/constants";

const SERVICES = [
  { key: "companionship", label: "Companionship" },
  { key: "personal_care", label: "Personal Care" },
  { key: "respite_care", label: "Respite Care" },
  { key: "dementia_support", label: "Dementia Support" },
  { key: "transportation", label: "Transportation" },
  { key: "other", label: "Other" },
];

export function ClientHome() {
  const [cases, setCases] = useState([]);
  const [loading, setLoading] = useState(true);

  const load = async () => {
    try {
      const { data } = await api.get("/cases");
      setCases(data);
    } catch (e) {
      toast.error(formatApiError(e));
    } finally {
      setLoading(false);
    }
  };

  useEffect(() => {
    load();
    const t = setInterval(load, 15000);
    return () => clearInterval(t);
  }, []);

  return (
    <PortalLayout title="My Care Requests">
      <div className="flex flex-wrap items-center justify-between gap-3 mb-6">
        <p className="text-sm text-[#64748B]">
          Track every request, every step of the way. Updates refresh automatically.
        </p>
        <Link to="/client/new" data-testid="client-new-request">
          <Button className="bg-[#AC9667] hover:bg-[#7A6A48] text-white rounded-full h-11 px-5">
            <Plus className="w-4 h-4 mr-1.5" /> Request Care
          </Button>
        </Link>
      </div>

      {loading ? (
        <div className="py-20 text-center text-[#64748B] text-sm">Loading...</div>
      ) : cases.length === 0 ? (
        <div data-testid="client-empty" className="text-center py-20 bg-white rounded-3xl border border-[#E5DDC9]">
          <p className="font-serif text-2xl text-[#005165] mb-2">No care requests yet</p>
          <p className="text-sm text-[#64748B] mb-6 max-w-sm mx-auto">
            Submit a request — it takes about 3 minutes. Our team will respond within one business day.
          </p>
          <Link to="/client/new">
            <Button className="bg-[#005165] text-[#FFFFFF] rounded-full h-12 px-7">
              <Plus className="w-4 h-4 mr-2" /> Submit your first request
            </Button>
          </Link>
        </div>
      ) : (
        <div className="grid gap-4 sm:gap-5">
          {cases.map((c) => (
            <article
              key={c.id}
              data-testid={`case-card-${c.id}`}
              className="bg-white rounded-2xl border border-[#E5DDC9] p-5 sm:p-6"
            >
              <div className="flex flex-col sm:flex-row sm:items-start sm:justify-between gap-3 mb-4">
                <div>
                  <p className="text-xs uppercase tracking-wider text-[#64748B]">Care for</p>
                  <p className="font-serif text-2xl text-[#005165]">{c.care_recipient_name}</p>
                  <p className="text-xs text-[#64748B] mt-1">Submitted {formatDate(c.created_at)}</p>
                </div>
                <StatusPill status={c.status} urgent={c.urgent} />
              </div>
              <div className="flex flex-wrap gap-2 mb-4">
                {c.services?.map((s) => (
                  <span key={s} className="px-3 py-1 rounded-full text-xs bg-[#F8F9FA] text-[#005165] border border-[#E5DDC9]">
                    {SERVICE_LABELS[s] || s}
                  </span>
                ))}
              </div>
              {c.notes && (
                <p className="text-sm text-[#4B5563] italic">"{c.notes}"</p>
              )}
              {c.status === "assigned" || c.status === "in_progress" ? (
                <p className="mt-3 text-xs text-emerald-700">
                  A caregiver has been matched. We'll reach out to coordinate the start date.
                </p>
              ) : null}
              {c.admin_notes && (
                <div className="mt-3 p-3 rounded-lg bg-[#F8F9FA]/50 border border-[#E5DDC9] text-sm text-[#1F2937]">
                  <p className="text-[10px] uppercase tracking-wider text-[#AC9667] mb-1">Update from our team</p>
                  {c.admin_notes}
                </div>
              )}
            </article>
          ))}
        </div>
      )}

      <div className="mt-8 p-5 rounded-2xl bg-[#005165] text-[#FFFFFF] flex flex-col sm:flex-row items-start sm:items-center gap-4">
        <Phone className="w-5 h-5 text-[#C4B088]" />
        <div className="flex-1">
          <p className="text-sm">Need urgent assistance?</p>
          <a href={`tel:${BRAND.phoneRaw}`} className="font-serif text-xl text-[#C4B088]">{BRAND.phone}</a>
        </div>
      </div>
    </PortalLayout>
  );
}

export function ClientNewRequest() {
  const navigate = useNavigate();
  const [loading, setLoading] = useState(false);
  const [done, setDone] = useState(false);
  const [form, setForm] = useState({
    care_recipient_name: "",
    relationship: "",
    services: [],
    urgent: false,
    preferred_start_date: "",
    frequency: "",
    notes: "",
    address: "",
  });
  const update = (k, v) => setForm((f) => ({ ...f, [k]: v }));
  const toggleService = (k) =>
    setForm((f) => ({
      ...f,
      services: f.services.includes(k) ? f.services.filter((s) => s !== k) : [...f.services, k],
    }));

  const submit = async (e) => {
    e.preventDefault();
    if (!form.care_recipient_name || !form.relationship || form.services.length === 0) {
      toast.error("Please complete name, relationship, and select at least one service.");
      return;
    }
    setLoading(true);
    try {
      await api.post("/cases", form);
      setDone(true);
    } catch (err) {
      toast.error(formatApiError(err));
    } finally {
      setLoading(false);
    }
  };

  if (done) {
    return (
      <PortalLayout>
        <div className="max-w-xl mx-auto text-center py-10 bg-white rounded-3xl border border-[#E5DDC9] p-10" data-testid="client-request-success">
          <div className="inline-flex w-14 h-14 rounded-full bg-emerald-50 border border-emerald-200 items-center justify-center mb-4">
            <CheckCircle2 className="w-7 h-7 text-emerald-600" />
          </div>
          <h2 className="font-serif text-3xl text-[#005165] mb-2">Request received.</h2>
          <p className="text-sm text-[#64748B] mb-6">
            {form.urgent ? "Marked urgent — we'll reach out within hours." : "We'll review and respond within one business day."}
          </p>
          <div className="flex flex-col sm:flex-row gap-3 justify-center">
            <Button onClick={() => navigate("/client")} className="rounded-full bg-[#005165] text-[#FFFFFF] h-11 px-6">
              View my requests
            </Button>
            <Button variant="outline" onClick={() => { setDone(false); setForm({ care_recipient_name: "", relationship: "", services: [], urgent: false, preferred_start_date: "", frequency: "", notes: "", address: "" }); }} className="rounded-full h-11 px-6 border-[#005165]/20">
              Submit another
            </Button>
          </div>
        </div>
      </PortalLayout>
    );
  }

  return (
    <PortalLayout>
      <button onClick={() => navigate(-1)} className="inline-flex items-center gap-2 text-sm text-[#64748B] hover:text-[#AC9667] mb-6">
        <ArrowLeft className="w-4 h-4" /> Back
      </button>

      <div className="max-w-2xl">
        <h1 className="font-serif text-3xl sm:text-4xl text-[#005165]">Request care</h1>
        <p className="text-sm text-[#64748B] mt-1 mb-8">Takes about 3 minutes. Everything is confidential.</p>

        <form onSubmit={submit} data-testid="client-request-form" className="bg-white rounded-3xl border border-[#E5DDC9] p-6 sm:p-8 space-y-6">
          {/* Urgency switch — top of form for visibility */}
          <div className={`flex items-center justify-between gap-4 p-4 rounded-2xl border-2 ${form.urgent ? "bg-rose-50 border-rose-300" : "bg-[#FFFFFF] border-[#E5DDC9]"}`}>
            <div className="flex items-start gap-3">
              <AlertTriangle className={`w-5 h-5 mt-0.5 ${form.urgent ? "text-rose-600" : "text-[#64748B]"}`} />
              <div>
                <p className="font-medium text-[#005165]">Mark as urgent</p>
                <p className="text-xs text-[#64748B]">Care needed within 24–48 hours (post-hospital, emergency respite, etc.)</p>
              </div>
            </div>
            <Switch
              data-testid="urgent-switch"
              checked={form.urgent}
              onCheckedChange={(v) => update("urgent", v)}
              className="data-[state=checked]:bg-rose-600"
            />
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cr-name">Care Recipient's Name *</Label>
              <Input id="cr-name" data-testid="cr-name" value={form.care_recipient_name} onChange={(e) => update("care_recipient_name", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
            <div>
              <Label htmlFor="cr-rel">Your Relationship *</Label>
              <Input id="cr-rel" data-testid="cr-rel" placeholder="e.g. Daughter, Spouse" value={form.relationship} onChange={(e) => update("relationship", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
            <div className="sm:col-span-2">
              <Label htmlFor="cr-addr">Address / Neighborhood (Madison area)</Label>
              <Input id="cr-addr" data-testid="cr-addr" value={form.address} onChange={(e) => update("address", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
          </div>

          <div>
            <Label className="mb-3 block">Services needed *</Label>
            <div className="grid sm:grid-cols-2 gap-2">
              {SERVICES.map((s) => {
                const checked = form.services.includes(s.key);
                return (
                  <button
                    type="button"
                    key={s.key}
                    data-testid={`cr-svc-${s.key}`}
                    onClick={() => toggleService(s.key)}
                    className={`flex items-center gap-3 p-3 rounded-xl border text-left transition-colors ${
                      checked ? "bg-[#005165] border-[#005165] text-[#FFFFFF]" : "bg-[#FFFFFF] border-[#E5DDC9] text-[#005165] hover:border-[#AC9667]"
                    }`}
                  >
                    <span
                      aria-hidden="true"
                      className={`w-5 h-5 rounded-md border flex items-center justify-center shrink-0 ${
                        checked ? "bg-[#AC9667] border-[#AC9667]" : "bg-white border-[#AC9667]/50"
                      }`}
                    >
                      {checked && (
                        <svg viewBox="0 0 16 16" className="w-3.5 h-3.5 text-white" fill="none" stroke="currentColor" strokeWidth="3">
                          <polyline points="3 8 7 12 13 4" strokeLinecap="round" strokeLinejoin="round" />
                        </svg>
                      )}
                    </span>
                    <span className="text-sm font-medium">{s.label}</span>
                  </button>
                );
              })}
            </div>
          </div>

          <div className="grid sm:grid-cols-2 gap-4">
            <div>
              <Label htmlFor="cr-freq">Frequency</Label>
              <Select value={form.frequency} onValueChange={(v) => update("frequency", v)}>
                <SelectTrigger id="cr-freq" data-testid="cr-freq" className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]">
                  <SelectValue placeholder="Select frequency" />
                </SelectTrigger>
                <SelectContent>
                  <SelectItem value="A few hours/week">A few hours/week</SelectItem>
                  <SelectItem value="Daily (part-time)">Daily (part-time)</SelectItem>
                  <SelectItem value="Daily (full-time)">Daily (full-time)</SelectItem>
                  <SelectItem value="24/7 Live-in">24/7 Live-in</SelectItem>
                  <SelectItem value="Respite (occasional)">Respite (occasional)</SelectItem>
                  <SelectItem value="Not sure yet">Not sure yet</SelectItem>
                </SelectContent>
              </Select>
            </div>
            <div>
              <Label htmlFor="cr-start">Preferred Start Date</Label>
              <Input id="cr-start" data-testid="cr-start" type="date" value={form.preferred_start_date} onChange={(e) => update("preferred_start_date", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
            </div>
          </div>

          <div>
            <Label htmlFor="cr-notes">Anything else we should know?</Label>
            <Textarea id="cr-notes" data-testid="cr-notes" rows={4} value={form.notes} onChange={(e) => update("notes", e.target.value)} className="mt-1.5 bg-[#FFFFFF] border-[#E5DDC9]" />
          </div>

          <Button type="submit" disabled={loading} data-testid="cr-submit" className="w-full sm:w-auto bg-[#005165] hover:bg-[#003946] text-[#FFFFFF] rounded-full h-12 px-8 btn-shimmer">
            {loading ? "Submitting..." : "Submit Request"}
          </Button>
        </form>
      </div>
    </PortalLayout>
  );
}
